import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from 'components/ui/Header';
import Breadcrumb from 'components/ui/Breadcrumb';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import WelcomeHeader from './components/WelcomeHeader';
import ProfileOverviewCard from './components/ProfileOverviewCard';
import QuickActionCards from './components/QuickActionCards';
import StatusProgressPanel from './components/StatusProgressPanel';
import RecentActivityPanel from './components/RecentActivityPanel';
import { useAuth } from '../../contexts/AuthContext';

const MOCK_ENROLLED_COURSES = [
  {
    id: 1,
    name: "Volunteer Orientation",
    progress: 40,
    status: "in_progress",
    totalModules: 5,
    completedModules: 2,
  },
  {
    id: 2,
    name: "Community Safety Basics",
    progress: 0,
    status: "enrolled",
    totalModules: 4,
    completedModules: 0,
  },
];

const MOCK_ACTIVITIES = [
  { id: 1, type: "registration", description: "Successfully registered as a new volunteer.", time: "Feb 28, 2026 · 9:15 AM" },
  { id: 2, type: "enrollment", description: "Enrolled in Volunteer Orientation course.", time: "Mar 01, 2026 · 11:30 AM" },
  { id: 3, type: "enrollment", description: "Enrolled in Community Safety Basics.", time: "Mar 01, 2026 · 11:45 AM" },
  { id: 4, type: "update", description: "Profile information updated.", time: "Mar 02, 2026 · 8:00 AM" },
];

const MOCK_ANNOUNCEMENTS = [
  {
    id: 1,
    title: "Spring Volunteer Drive – March 2026",
    message: "We are looking for new volunteers to join our spring community outreach program. Sign up before March 15!",
    date: "Mar 01, 2026",
  },
  {
    id: 2,
    title: "Orientation Schedule Updated",
    message: "The March 10 orientation session has moved online. Check your email for the meeting link.",
    date: "Feb 27, 2026",
  },
];

export default function VolunteerDashboard() {
  const navigate = useNavigate();
  const { user, loading, isAuthenticated, signOut } = useAuth();

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login-screen', { replace: true });
    }
  }, [loading, isAuthenticated, navigate]);

  const handleLogout = async () => {
    await signOut();
    navigate('/login-screen');
  };

  // Build volunteer object from auth user metadata
  const volunteer = user ? {
    id: user?.id,
    name: user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')?.[0] || 'Volunteer',
    email: user?.email,
    mobile: user?.user_metadata?.mobile || '',
    dob: user?.user_metadata?.dob || '',
    school: user?.user_metadata?.school || '',
    status: 'new',
    joinDate: new Date(user?.created_at)?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
  } : null;

  // Loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm font-caption text-muted-foreground">Verifying your session…</p>
        </div>
      </div>
    );
  }

  // Don't render dashboard content if not authenticated (redirect in progress)
  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        isAuthenticated={true}
        user={{ name: volunteer?.name, email: volunteer?.email, status: volunteer?.status }}
        onLogout={handleLogout}
      />
      <main className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8">
        {/* Breadcrumb */}
        <div className="mb-4">
          <Breadcrumb />
        </div>

        {/* Welcome Header */}
        <div className="mb-6">
          <WelcomeHeader volunteer={volunteer} />
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left / Main Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Profile Overview */}
            <ProfileOverviewCard volunteer={volunteer} />

            {/* Quick Actions */}
            <div>
              <h2 className="text-base md:text-lg font-heading font-semibold text-foreground flex items-center gap-2 mb-3">
                <Icon name="Zap" size={18} color="var(--color-accent)" />
                Quick Actions
              </h2>
              <QuickActionCards volunteerStatus={volunteer?.status} />
            </div>

            {/* Status Progress */}
            <StatusProgressPanel volunteer={volunteer} enrolledCourses={MOCK_ENROLLED_COURSES} />

            {/* CTA Banner for New Volunteers */}
            {volunteer?.status === 'new' && (
              <div className="bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                    <Icon name="Lightbulb" size={20} color="var(--color-accent)" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-heading font-semibold text-foreground">Ready to get trained?</p>
                    <p className="text-xs font-caption text-muted-foreground mt-0.5">
                      Complete orientation to advance from New to Trained status and unlock more opportunities.
                    </p>
                  </div>
                </div>
                <Button
                  variant="warning"
                  size="sm"
                  iconName="BookOpen"
                  iconPosition="left"
                  iconSize={14}
                  onClick={() => navigate('/training-courses-catalog')}
                  className="flex-shrink-0"
                >
                  Start Training
                </Button>
              </div>
            )}
          </div>

          {/* Right / Secondary Column */}
          <div className="lg:col-span-1">
            <RecentActivityPanel activities={MOCK_ACTIVITIES} announcements={MOCK_ANNOUNCEMENTS} />
          </div>
        </div>
      </main>
      {/* Footer */}
      <footer className="border-t border-border bg-card mt-12">
        <div className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-primary flex items-center justify-center">
              <Icon name="Heart" size={13} color="white" />
            </div>
            <span className="text-sm font-heading font-semibold text-foreground">VolunteerHub</span>
          </div>
          <p className="text-xs font-caption text-muted-foreground text-center">
            &copy; {new Date()?.getFullYear()} VolunteerHub. All rights reserved.
          </p>
          <div className="flex items-center gap-1 text-xs font-caption text-muted-foreground">
            <Icon name="ShieldCheck" size={13} color="var(--color-success)" />
            SSL Secured
          </div>
        </div>
      </footer>
    </div>
  );
}